/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: ObjectBaseComponent
 * Reason: Root OBC of the read-only OVERVIEW of one cr.CustomerBankAccount (TM-1000002030): the base data
 *         of the bank account and the billing accounts whose assignment slices (bm.CustomerBillingAccountBank)
 *         point at it, with their periods. Layer 1 of the three-layer pattern of
 *         `cp-provision-type-matrix-object`:
 *           view/base  -> {@link CrCustomerBankAccountOverviewBaseComponent} (the single navigation item)
 *           view/full  -> {@link CrCustomerBankAccountOverviewFullComponent}
 *           view/side  -> {@link CrCustomerBankAccountOverviewSideComponent}
 *           shared/    -> {@link CrCustomerBankAccountOverviewComponent}, the two widgets
 *
 * ── HOW IT IS REACHED (FD-1 / S-1)
 *
 *   ONLY through `viewType = 'overview'` (`CR_BANK_ACCOUNT_VIEW_TYPE_OVERVIEW`):
 *   `CustomerBankAccountService.getObjectComponent()` answers this class for every mode except create when
 *   that viewType is set. The Abrechnungs-Landkarte (TM-1000002027) opens it with
 *   `crBuildBankAccountOverviewNavigationEntry(id, label)` + `navigateTo(entry, 'right')`, i.e. in the RIGHT
 *   drawer with `uiMode = 'side'` - so `side` is the mode that carries the traffic. Every open without the
 *   viewType keeps the change OBC (CANNOT 1-3).
 *
 * ── READ-ONLY (FD-3 / S-2)
 *
 *   No create, edit, copy or replace affordance anywhere in this OBC. Bank accounts and billing accounts are
 *   captured and changed only through the wizards (user decision 06.10.2026).
 *
 * ── LIFECYCLE
 *
 *   The `dto` is loaded by {@link ObjectBaseComponent} and nowhere else. {@link onObjectChanged} only hands
 *   the drawer title to the frame; it never calls `refreshObject()` (G2.5 - that would loop). The widgets are
 *   built by the shared component from `objectIdentifier.objectId`, once per (id, compact) pair (AU-9).
 *
 * UI text German with real umlauts, identifiers and comments English.
 *
 * @ticket TM-1000002030
 */
import {Component, OnInit} from '@angular/core';
import {ObjectBaseComponent} from 'features/core/shared/object/mvs-object-base/object-base.component';
import type {CustomerBankAccountDto} from '../../../model/dto/entity/customer-bank-account.dto';
import {buildCrBankAccountLabel} from '../../../logic/cr-bank-account-label';

@Component({
    selector: 'cr-customer-bank-account-overview-object',
    templateUrl: './cr-customer-bank-account-overview-object.component.html',
    styleUrls: ['./cr-customer-bank-account-overview-object.component.scss'],
    standalone: false
})
export class CrCustomerBankAccountOverviewObjectComponent extends ObjectBaseComponent implements OnInit {

    /**
     * The loaded bank account has changed: hand its label („IBAN · Bank · Kontoinhaber", full IBAN per
     * Q-11c) to the frame, which shows it as the drawer title (`onNameEvent`).
     *
     * Nothing else happens here. The views receive `dto` / `objectIdentifier` as bindings and the shared
     * component rebuilds its widgets only when the id changes. `refreshObject()` must NEVER be called here.
     */
    override onObjectChanged(): void {
        this.handleName(buildCrBankAccountLabel(this.dto as CustomerBankAccountDto));
    }

}
